import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import Seo from "@/components/Seo";
import { API } from "@/lib/api";
import { Check, X, Minus, ArrowRight, SealCheck, ChatCircleText, CurrencyGbp, Stack } from "@phosphor-icons/react";

const ICONS = { SealCheck, ChatCircleText, CurrencyGbp, Stack };

// Built-in fallback so the page renders instantly and never breaks if the API is unavailable.
// The live copy is served from the database via GET /api/compare (auto-refreshed monthly).
const DEFAULTS = {
  competitors: { easy: { name: "EasyAccrue" }, spread: { name: "Spread" } },
  why: [
    { icon: "SealCheck", title: "Sign-off you can trust", body: "Two-person approvals, balance-variance accuracy checks and month-end close packs — controls the others simply don't have." },
    { icon: "ChatCircleText", title: "Built for the whole team", body: "Review notes, @mentions and a dashboard of open queries keep preparer and reviewer on the same page, in-app." },
    { icon: "Stack", title: "Made for portfolios", body: "Run every client organisation from one portal with isolated charts of accounts, plus 12-month forecasting across them all." },
    { icon: "CurrencyGbp", title: "Transparent pricing", body: "Tiered plans from £39/month with every feature included — the more companies you run, the less you pay per company. Two months free annually. No quotes, no surprises." },
  ],
  rows: [
    { section: "Core" },
    { label: "Live Xero integration", wwl: true, easy: true, spread: true },
    { label: "Auto-built schedules from invoices & bills", wwl: true, easy: "partial", spread: true },
    { label: "Accruals", wwl: true, easy: true, spread: true },
    { label: "Prepayments", wwl: true, easy: true, spread: true },
    { label: "Deferred income / revenue recognition", wwl: true, easy: false, spread: true },
    { label: "Recurring accrual engine", wwl: true, easy: "partial", spread: true },
    { label: "Fixed-asset depreciation schedules", wwl: true, easy: false, spread: false },
    { section: "Reporting & control" },
    { label: "12-month forecasting", wwl: true, easy: false, spread: "partial" },
    { label: "Reconciliation to Xero ledger balances", wwl: true, easy: "partial", spread: true },
    { label: "Balance-variance accuracy checks", wwl: true, easy: false, spread: "partial" },
    { label: "Month-end close sign-off packs", wwl: true, easy: false, spread: false },
    { label: "Branded PDF & Excel exports", wwl: true, easy: "partial", spread: "partial" },
    { label: "Full audit history", wwl: true, easy: true, spread: true },
    { label: "Compliance tagging (FRS 102 / FRS 105)", wwl: true, easy: false, spread: false },
    { section: "Collaboration & scale" },
    { label: "Review notes & @mentions", wwl: true, easy: false, spread: false },
    { label: "Two-person approvals", wwl: true, easy: false, spread: false },
    { label: "Multi-entity portfolio, isolated charts of accounts", wwl: true, easy: "partial", spread: true },
    { label: "Unlimited team members", wwl: true, easy: "partial", spread: true },
    { section: "Pricing" },
    { label: "Pricing model", text: true, wwl: "Tiered from £39/mo — all features", easy: "Free tier; Solo €15 / Team €50 per mo (ex VAT)", spread: "£79/mo + £29 per extra org (ex VAT)" },
    { label: "Lower cost per company as you scale", wwl: true, easy: false, spread: "partial" },
    { label: "Annual plan — 2 months free", wwl: true, easy: false, spread: false },
    { label: "Free trial", text: true, wwl: "7 days, no card", easy: "Free tier (limited)", spread: "7-day free trial" },
  ],
  footnote: "Comparison based on publicly available information about EasyAccrue and Spread. Competitor features and pricing may change — please verify directly with each provider. EasyAccrue and Spread are trademarks of their respective owners.",
};

function Cell({ v }) {
  if (v === true) return <Check size={20} weight="bold" className="mx-auto text-positive" />;
  if (v === false) return <X size={18} weight="bold" className="mx-auto text-navy/25" />;
  if (v === "partial") return <Minus size={18} weight="bold" className="mx-auto text-warn" title="Limited" />;
  if (v === "soon") return <span className="text-xs font-semibold text-[#F37A24]">Coming soon</span>;
  return <span className="text-sm text-navy/70">{v}</span>;
}

export default function Compare() {
  const [data, setData] = useState(DEFAULTS);

  useEffect(() => {
    fetch(`${API}/compare`)
      .then((r) => r.json())
      .then((d) => { if (d && Array.isArray(d.rows) && d.rows.length) setData({ ...DEFAULTS, ...d }); })
      .catch(() => {});
  }, []);

  const easyName = data.competitors?.easy?.name || "EasyAccrue";
  const spreadName = data.competitors?.spread?.name || "Spread";

  return (
    <MarketingLayout>
      <Seo title="How Lottie Compares" path="/compare"
        description="How WorkWithLottie compares to spreadsheets and manual month-end for accruals, prepayments and deferred income in Xero — automation, accuracy, audit trail and portfolio scale." />
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="overline mb-4" style={{ color: "#F37A24" }}>Compare</div>
        <h1 className="font-heading font-black text-navy text-4xl sm:text-5xl tracking-tighter">
          How WorkWithLottie stacks up
        </h1>
        <p className="text-navy/70 text-base sm:text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
          A side-by-side look at functionality and cost against {easyName} and {spreadName} — so you can
          see exactly what your practice gets for the money.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-4" data-testid="why-lottie">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(data.why || []).map((w) => {
            const Icon = ICONS[w.icon] || SealCheck;
            return (
              <div key={w.title} className="card-surface p-6" data-testid={`why-card-${w.title}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(242,111,33,0.1)" }}>
                  <Icon size={22} weight="duotone" style={{ color: "#F37A24" }} />
                </div>
                <h3 className="font-heading font-bold text-navy mb-2">{w.title}</h3>
                <p className="text-navy/70 text-sm leading-relaxed">{w.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pt-12">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-card bg-white">
          <table className="w-full min-w-[640px] text-sm" data-testid="compare-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left px-5 py-4 font-semibold text-navy/70 overline">Feature</th>
                <th className="px-4 py-4 text-center w-[170px]" style={{ background: "rgba(242,111,33,0.06)" }}>
                  <img src="/lottie-logo.png" alt="WorkWithLottie" data-testid="compare-lottie-logo"
                    className="h-9 w-auto object-contain mx-auto" />
                  <div className="text-[11px] font-semibold mt-1" style={{ color: "#F37A24" }}>Recommended</div>
                </th>
                <th className="px-4 py-4 text-center w-[150px] font-heading font-bold text-navy" data-testid="compare-col-easy">{easyName}</th>
                <th className="px-4 py-4 text-center w-[150px] font-heading font-bold text-navy" data-testid="compare-col-spread">{spreadName}</th>
              </tr>
            </thead>
            <tbody>
              {(data.rows || []).map((r, i) =>
                r.section ? (
                  <tr key={`s-${i}`} className="bg-slate-50/70" data-testid={`compare-section-${r.section}`}>
                    <td colSpan={4} className="px-5 py-2.5 overline text-navy/65">{r.section}</td>
                  </tr>
                ) : (
                  <tr key={r.label} className="border-t border-slate-100" data-testid={`compare-row-${i}`}>
                    <td className="px-5 py-3 text-navy/80">{r.label}</td>
                    <td className="px-4 py-3 text-center" style={{ background: "rgba(242,111,33,0.05)" }}><Cell v={r.wwl} /></td>
                    <td className="px-4 py-3 text-center"><Cell v={r.easy} /></td>
                    <td className="px-4 py-3 text-center"><Cell v={r.spread} /></td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-center gap-6 mt-5 text-xs text-navy/70">
          <span className="flex items-center gap-1.5"><Check size={15} weight="bold" className="text-positive" /> Included</span>
          <span className="flex items-center gap-1.5"><Minus size={15} weight="bold" className="text-warn" /> Limited</span>
          <span className="flex items-center gap-1.5"><X size={14} weight="bold" className="text-navy/25" /> Not available</span>
        </div>

        <p className="text-navy/65 text-xs text-center mt-6 max-w-3xl mx-auto leading-relaxed" data-testid="compare-footnote">
          {data.footnote}
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">See the difference for yourself</h2>
        <p className="text-navy/70 mt-4 max-w-xl mx-auto">Connect Xero and run a full month-end on us — free for 7 days.</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          <Link to="/start" data-testid="compare-cta-trial" className="text-white font-semibold px-6 py-3.5 rounded-xl flex items-center gap-2" style={{ background: "#F37A24" }}>
            Start free trial <ArrowRight size={18} weight="bold" />
          </Link>
          <Link to="/pricing" data-testid="compare-cta-pricing" className="bg-white text-navy font-semibold px-6 py-3.5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
            View pricing
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
